import type {Expression} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  getAnn,
} from '@packages/utilities/contextual-expression';
import type {
  Type,
  TypeCheckerError,
  CheckerState,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {print} from '@packages/systems/wfdl/codegen/wfdl';
import {toHumanReadableString} from '@packages/systems/wfdl-Qualified';
import {
  inferType,
  checkType,
  getErrors,
  getTypeFromAny,
  Types,
} from '@packages/systems/wfdl';
import {getExpressionFromAny} from '@packages/systems/wfdl-ast';

type CheckerWithErrors<a> = (
  arg1: CheckerState
) => [CheckerState, a, Array<WfdlStaticError>];

const isExpr = (obj: any) => getExpressionFromAny(obj) !== null;
const isType = (obj: any) => getTypeFromAny(obj) !== null;
const printType = Types.print;
const printPath = JSON.stringify;

// [FIXME]
// This is just a quick hack function.
// Add a real single line pretty print to tools.
function printExpr(expr: Expression) {
  const str = print(expr);
  if (str.length > 100) return str;

  const lines = str.split('\n');
  let sb = '';
  for (let i = 0, len = lines.length; i < len; i++) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    let line = lines[i].trim();
    const lineLastCharIdx = line.length - 1;
    const hasTrailingComma = line[lineLastCharIdx] === ',';
    if (hasTrailingComma && i + 1 < len) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      const nextLine = lines[i + 1].trim();
      if (nextLine === '}' || nextLine === ']') {
        // String#slice is upperbound exclusive.
        line = line.slice(0, lineLastCharIdx);
      }
    }
    sb += line;
    if (i + 1 !== len) sb += ' ';
  }
  return sb;
}

function readType(ctxExpr: ContextualExpression): Type {
  const ty = getAnn(ctxExpr).type;
  if (ty == null) {
    const exprString = JSON.stringify(ctxExpr) as any;
    throw new TypeError(
      `Could not read 'type' from contextual expression: ${exprString}`
    );
  }
  return ty;
}

type WfdlTypeErrorType = 'TypesDoNotUnify' | 'MissingField' | 'ExtraField';

function unimplementedError(errorType: string) {
  return new TypeError(`Unimplemented typechecking error ${errorType}`);
}

class WfdlTypeError extends TypeError {
  type: WfdlTypeErrorType;
  context: ErrorContext | null;
  suggestions: Array<string> | null;

  constructor(
    type: WfdlTypeErrorType,
    context: ErrorContext | null,
    suggestions: Array<string> | null,
    msg: string
  ) {
    super(msg);
    this.type = type;
    this.context = context;
    this.suggestions = suggestions;
  }
}

type WfdlReferenceErrorType = 'UndefinedValue' | 'UndefinedComponent';
class WfdlReferenceError extends ReferenceError {
  type: WfdlReferenceErrorType;
  context: ErrorContext | null;

  constructor(
    type: WfdlReferenceErrorType,
    context: ErrorContext | null,
    msg: string
  ) {
    super(msg);
    this.type = type;
    this.context = context;
  }
}

type WfdlStaticError = WfdlTypeError | WfdlReferenceError;

export function assertType(
  expr: Expression,
  type: Type
): CheckerWithErrors<ContextualExpression> {
  return function assertTypeWithContext(
    context: CheckerState
  ): [CheckerState, ContextualExpression, Array<WfdlStaticError>] {
    const [checkerState, ctxExpr] = checkType(expr, type)(context);
    // [TODO]
    // I think I could get rid of the call to inferType.
    // ctxExpr.expr should have all the pieces?
    const inferCheck = inferType(expr)(context);
    const inferred = inferCheck[1];

    const expectedType = readType(ctxExpr);
    const inferredType = readType(inferred);

    const detectedErrors = getErrors(checkerState);
    const errors = detectedErrors.map((err) => {
      return createError(expr, expectedType, inferredType, err);
    });

    return [checkerState, ctxExpr, errors];
  };
}

type ErrorContext = {
  value: Expression;
  path: ExpressionPath;
  inferredType?: Type;
  expectedType?: Type;
};

function formatErrorMessage({
  message,
  context,
  suggestions,
}: {
  message: string;
  context?: ErrorContext;
  suggestions?: Array<string>;
}) {
  let errorString = `${message}\n`;

  if (context) {
    errorString += `\n`;
    errorString += `Context:\n`;
    errorString += '--------\n';
    errorString += `Path: ${printPath(context.path)}\n`;
    errorString += `Value: ${printExpr(context.value)}\n`;

    if (context.inferredType) {
      errorString += `Inferred type: ${printType(context.inferredType)}\n`;
    }

    if (context.expectedType) {
      errorString += `Expected type: ${printType(context.expectedType)}\n`;
    }
  }

  if (suggestions) {
    errorString += `\n`;
    errorString += `Suggestion:\n`;
    errorString += '--------\n';
    for (const sug of suggestions) {
      errorString += `- ${sug}\n`;
    }
  }
  return errorString;
}

function fmt(strings: Array<string>, ...vals: Array<any>) {
  let sb = ``;
  for (let i = 0; i < strings.length; i++) {
    const str = strings[i];
    sb += str;
    const val = vals[i];
    if (val) {
      if (isExpr(val)) {
        sb += `\`${printExpr(val)}\``;
      } else if (isType(val)) {
        sb += `\`${printType(val)}\``;
      } else {
        sb += `\`${val}\``;
      }
    }
  }
  return sb;
}

function mismatchedType(expected: Type, received: Type) {
  // @ts-expect-error - TS2345 - Argument of type 'TemplateStringsArray' is not assignable to parameter of type 'string[]'.
  let errorString = fmt`Expected: ${expected}\n`;
  // @ts-expect-error - TS2345 - Argument of type 'TemplateStringsArray' is not assignable to parameter of type 'string[]'.
  errorString += fmt`Received: ${received}\n`;
  return errorString;
}

function createError(
  expr: Expression,
  expectedType: Type,
  inferredType: Type,
  err: TypeCheckerError
) {
  switch (err.type) {
    case 'TypesDoNotUnify': {
      const context = {value: expr, inferredType, path: err.val.path} as const;
      const msg = formatErrorMessage({
        message: `Mismatched type \n${mismatchedType(
          err.val.types[1],
          err.val.types[0]
        )}`,
        context,
      });
      return new WfdlTypeError(err.type, context, null, msg);
    }
    case 'ExtraField': {
      const context = {
        value: expr,
        inferredType,
        expectedType,
        path: err.val.path,
      } as const;
      const suggestions = [
        // @ts-expect-error - TS2345 - Argument of type 'TemplateStringsArray' is not assignable to parameter of type 'string[]'.
        fmt`Remove field ${err.val.label} from ${expr}`,
        // @ts-expect-error - TS2345 - Argument of type 'TemplateStringsArray' is not assignable to parameter of type 'string[]'.
        fmt`Add field ${err.val.label} to ${expectedType}`,
      ];
      const msg = formatErrorMessage({
        // @ts-expect-error - TS2345 - Argument of type 'TemplateStringsArray' is not assignable to parameter of type 'string[]'.
        message: fmt`Expected record type ${expectedType} does not have field named ${err.val.label}`,
        context,
        suggestions,
      });
      return new WfdlTypeError(err.type, context, suggestions, msg);
    }
    case 'MissingField': {
      const context = {
        value: expr,
        inferredType,
        expectedType,
        path: err.val.path,
      } as const;
      // @ts-expect-error - TS2345 - Argument of type 'TemplateStringsArray' is not assignable to parameter of type 'string[]'.
      const suggestions = [fmt`Add field ${err.val.label} to ${expr}`];
      const msg = formatErrorMessage({
        // @ts-expect-error - TS2345 - Argument of type 'TemplateStringsArray' is not assignable to parameter of type 'string[]'.
        message: fmt`Expected the field ${err.val.label} but it is missing in ${expr}`,
        context,
        suggestions,
      });
      return new WfdlTypeError(err.type, context, suggestions, msg);
    }
    case 'UndefinedComponent': {
      const msg = formatErrorMessage({
        message: `Could not find component: ${toHumanReadableString(
          err.val.identifier
        )}`,
      });
      return new WfdlReferenceError(err.type, null, msg);
    }
    case 'UndefinedValue': {
      const context = {
        value: expr,
        inferredType,
        expectedType,
        path: err.val.path,
      } as const;
      const msg = formatErrorMessage({
        message: `Could not find value: ${toHumanReadableString(
          // @ts-expect-error — says VariableType is incompatible with Qualified, but flow isn't complaining about UndefinedComponent...
          err.val.identifier
        )}`,
      });
      return new WfdlReferenceError(err.type, context, msg);
    }

    // TODO
    case 'InfiniteType': {
      throw unimplementedError('InfiniteType');
    }
    case 'RigidOutsideScope': {
      throw unimplementedError('RigidOutsideScope');
    }
    default: {
      //(err.type: empty);
      throw new TypeError(`Unknown typechecking error: ${err.type}`);
    }
  }
}
